<template>
    <div class="rpe-layout">
        <!-- Modules -->
        <aside class="rpe-sidebar">
            <div class="rpe-sidebar-header">
                <i class="fal fa-layer-group rpe-sidebar-header-icon"></i>
                <span>{{ $t('permissions.modules') }}</span>
            </div>
            <nav class="rpe-sidebar-nav">
                <button
                    v-for="group in mergedGroups"
                    :key="group.key"
                    class="rpe-sidebar-item"
                    :class="{ 'rpe-sidebar-item--active': selectedGroup === group.key }"
                    @click="selectedGroup = group.key"
                >
                    <i :class="group.icon" class="rpe-sidebar-item-icon"></i>
                    <span class="rpe-sidebar-item-label">{{ groupLabel(group) }}</span>
                    <span class="rpe-sidebar-count">
                        {{ grantedCount(group) }}/{{ group.permissions.length }}
                    </span>
                </button>
            </nav>
        </aside>

        <!-- Permissions du module sélectionné -->
        <div class="rpe-main">
            <template v-if="activeGroup">
                <div class="rpe-group-header">
                    <div class="rpe-group-info">
                        <div class="rpe-group-icon">
                            <i :class="activeGroup.icon"></i>
                        </div>
                        <div class="rpe-group-text">
                            <h3 class="rpe-group-title">{{ groupLabel(activeGroup) }}</h3>
                            <p class="rpe-group-sub">
                                <span class="rpe-group-sub-count">
                                    {{ grantedCount(activeGroup) }} / {{ activeGroup.permissions.length }}
                                </span>
                                {{ $t('permissions.permission_count') }}
                            </p>
                        </div>
                    </div>

                    <label v-if="hasGrantable(activeGroup)" class="rpe-select-all">
                        <span class="rpe-select-all-label">{{ selectAllLabel }}</span>
                        <span class="rpe-toggle">
                            <input
                                type="checkbox"
                                :checked="isGroupFullyGranted(activeGroup)"
                                :disabled="disabled"
                                @change="toggleGroup(activeGroup)"
                            />
                            <span class="rpe-toggle-slider"></span>
                        </span>
                    </label>
                </div>

                <ul class="rpe-permission-list">
                    <li
                        v-for="permission in activeGroup.permissions"
                        :key="permission.name"
                        class="rpe-permission-row"
                        :class="{ 'rpe-permission-row--modified': isModified(permission.name) }"
                    >
                        <div class="rpe-permission-body">
                            <span class="rpe-permission-label">
                                {{ permissionLabel(permission) }}
                                <i
                                    v-if="!permission.grantable"
                                    class="fal fa-lock rpe-permission-lock"
                                    :title="$t('roles.permission_not_grantable')"
                                ></i>
                            </span>
                            <code class="rpe-permission-key">{{ permission.name }}</code>
                            <span
                                v-if="requirementNote(permission)"
                                class="rpe-permission-note"
                            >
                                <i class="fal fa-link"></i>
                                {{ requirementNote(permission) }}
                            </span>
                        </div>
                        <label class="rpe-toggle">
                            <input
                                type="checkbox"
                                :checked="hasPermission(permission.name)"
                                :disabled="isToggleDisabled(permission)"
                                @change="togglePermission(permission, $event)"
                            />
                            <span class="rpe-toggle-slider"></span>
                        </label>
                    </li>
                </ul>
            </template>

            <div v-else class="rpe-empty">
                <i class="fal fa-hand-pointer rpe-empty-icon"></i>
                <p>{{ $t('permissions.select_module') }}</p>
            </div>

            <div class="rpe-footer">
                <div v-if="changesCount > 0" class="rpe-changes">
                    <i class="fal fa-pen-to-square"></i>
                    {{ changesCount }} {{ $t('permissions.modifications') }}
                </div>
                <div v-else></div>

                <div class="rpe-footer-actions">
                    <BmvButton
                        variant="ghost"
                        size="md"
                        :disabled="!hasChanges || isSaving"
                        @click="reset"
                    >
                        {{ $t('common.reset') }}
                    </BmvButton>
                    <BmvButton
                        variant="primary"
                        size="md"
                        icon="fal fa-save"
                        :loading="isSaving"
                        :disabled="disabled || !hasChanges || isSaving"
                        @click="save"
                    >
                        {{ $t('permissions.table.save') }}
                    </BmvButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import {
    permissionRequirements,
    resolvePermissionRequirements,
    withGrantedPermissions
} from '@/config/permissions'
import { rolePermissions } from '@/helpers/roles'

export default {
    name: 'RolePermissionsEditor',
    components: { BmvButton },
    props: {
        role: {
            type: Object,
            required: true
        },
        /** Catalogue normalisé : [{ key, label, icon, permissions: [{ name, label, grantable }] }] */
        groups: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            granted: [],
            original: [],
            isSaving: false,
            selectedGroup: null
        }
    },
    computed: {
        mergedGroups() {
            return withGrantedPermissions(this.groups, rolePermissions(this.role))
        },
        activeGroup() {
            return this.mergedGroups.find((group) => group.key === this.selectedGroup) || null
        },
        /** « Tout activer » tant qu'il reste une case à cocher, sinon l'inverse. */
        selectAllLabel() {
            const key =
                this.activeGroup && this.isGroupFullyGranted(this.activeGroup)
                    ? 'permissions.table.deselect_all'
                    : 'permissions.table.select_all'
            return this.$t(key)
        },
        /** Index nom -> permission, tous groupes confondus. */
        permissionIndex() {
            const index = {}
            this.mergedGroups.forEach((group) => {
                group.permissions.forEach((permission) => {
                    index[permission.name] = permission
                })
            })
            return index
        },
        /**
         * Pour chaque permission requise, la liste des permissions accordées
         * qui en dépendent. Tant que cette liste n'est pas vide, la retirer
         * casserait l'écran de la permission dépendante : son interrupteur est
         * figé et la raison affichée.
         */
        requiredBy() {
            const map = {}
            this.granted.forEach((name) => {
                resolvePermissionRequirements([name], this.permissionIndex).forEach((required) => {
                    if (!map[required]) map[required] = []
                    map[required].push(name)
                })
            })
            return map
        },
        hasChanges() {
            return this.changesCount > 0
        },
        changesCount() {
            const before = new Set(this.original)
            const after = new Set(this.granted)
            let count = 0
            before.forEach((name) => {
                if (!after.has(name)) count += 1
            })
            after.forEach((name) => {
                if (!before.has(name)) count += 1
            })
            return count
        }
    },
    watch: {
        role: {
            handler() {
                this.reset()
            },
            deep: true,
            immediate: true
        },
        mergedGroups: {
            handler(groups) {
                if (!groups.length) return
                if (!groups.some((group) => group.key === this.selectedGroup)) {
                    this.selectedGroup = groups[0].key
                }
            },
            immediate: true
        }
    },
    methods: {
        reset() {
            const current = rolePermissions(this.role)
            this.granted = [...current]
            this.original = [...current]
        },
        groupLabel(group) {
            if (group.label) return group.label
            const key = `permissions.groups.${group.key}`
            const label = this.$t(key)
            return label === key ? group.key : label
        },
        /**
         * Libellé du serveur en priorité. Le repli i18n passe par l'objet
         * `permissions.list` : les clés contiennent un point (`posts.view`) et
         * vue-i18n découperait le chemin.
         */
        permissionLabel(permission) {
            if (permission.label) return permission.label
            const list = this.$t('permissions.list')
            if (list && typeof list === 'object' && list[permission.name]) {
                return list[permission.name]
            }
            return permission.name
        },
        hasPermission(name) {
            return this.granted.includes(name)
        },
        isModified(name) {
            return this.original.includes(name) !== this.granted.includes(name)
        },
        grantedCount(group) {
            return group.permissions.filter((permission) => this.granted.includes(permission.name))
                .length
        },
        hasGrantable(group) {
            return group.permissions.some((permission) => permission.grantable)
        },
        /** Les permissions non accordables sont ignorées par le « Tout ». */
        isGroupFullyGranted(group) {
            const grantable = group.permissions.filter((permission) => permission.grantable)
            return (
                grantable.length > 0 &&
                grantable.every((permission) => this.granted.includes(permission.name))
            )
        },
        /** Permissions bloquantes accordées qui s'appuient sur celle-ci. */
        lockedBy(permission) {
            return this.requiredBy[permission.name] || []
        },
        isToggleDisabled(permission) {
            return (
                this.disabled ||
                !permission.grantable ||
                this.lockedBy(permission).length > 0
            )
        },
        /**
         * Deux messages possibles sous un libellé : ce que la permission
         * entraîne quand on l'accorde, ou ce qui la retient quand on voudrait
         * la retirer. Le second l'emporte, c'est celui qui explique un
         * interrupteur figé.
         */
        requirementNote(permission) {
            const holders = this.lockedBy(permission)
            if (holders.length) {
                return this.$t('permissions.required_by', {
                    list: this.labelList(holders)
                })
            }
            const requires = permissionRequirements(permission)
            if (requires.length) {
                return this.$t('permissions.also_enables', {
                    list: this.labelList(requires)
                })
            }
            return ''
        },
        labelList(names) {
            return names
                .map((name) => this.permissionLabel(this.permissionIndex[name] || { name }))
                .join(', ')
        },
        togglePermission(permission, event) {
            if (this.isToggleDisabled(permission)) return
            const next = new Set(this.granted)
            if (event.target.checked) {
                next.add(permission.name)
                // Accorder une permission accorde ce dont elle dépend : sans ça,
                // son écran tomberait sur /403 au premier chargement.
                resolvePermissionRequirements([permission.name], this.permissionIndex).forEach(
                    (required) => next.add(required)
                )
            } else {
                next.delete(permission.name)
            }
            this.granted = Array.from(next)
        },
        toggleGroup(group) {
            if (this.disabled) return
            const next = new Set(this.granted)
            const fullyGranted = this.isGroupFullyGranted(group)
            group.permissions
                .filter((permission) => permission.grantable)
                .forEach((permission) => {
                    if (fullyGranted) {
                        // Une permission retenue par une autre reste accordée.
                        if (!this.lockedBy(permission).length) next.delete(permission.name)
                    } else {
                        next.add(permission.name)
                        resolvePermissionRequirements(
                            [permission.name],
                            this.permissionIndex
                        ).forEach((required) => next.add(required))
                    }
                })
            this.granted = Array.from(next)
        },
        save() {
            if (this.disabled || !this.hasChanges || this.isSaving) return
            this.isSaving = true
            this.$emit('save', {
                permissions: [...this.granted],
                callback: (success) => {
                    this.isSaving = false
                    if (success !== false) {
                        this.original = [...this.granted]
                    }
                }
            })
        }
    }
}
</script>

<style scoped>
/* ── Layout ── */
.rpe-layout {
    display: flex;
    width: 100%;
    min-height: 480px;
    background: var(--v2-surface-0);
}

/* ── Sidebar ── */
.rpe-sidebar {
    width: 250px;
    min-width: 250px;
    background: #f8fafc;
    border-right: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
}

.rpe-sidebar-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 20px 20px 16px;
    font-weight: 600;
    font-size: 0.8rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.rpe-sidebar-header-icon {
    font-size: 0.9rem;
    color: #94a3b8;
}

.rpe-sidebar-nav {
    display: flex;
    flex-direction: column;
    padding: 0 8px 16px;
    gap: 2px;
}

.rpe-sidebar-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: none;
    background: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    font-size: 0.875rem;
    color: #475569;
    text-align: left;
    width: 100%;
}

.rpe-sidebar-item:hover {
    background: #e2e8f0;
    color: #0f172a;
}

.rpe-sidebar-item--active {
    background: #0891b2;
    color: #fff;
    font-weight: 500;
}

.rpe-sidebar-item--active:hover {
    background: #0e7490;
    color: #fff;
}

.rpe-sidebar-item--active .rpe-sidebar-item-icon {
    color: #fff;
}

.rpe-sidebar-item-icon {
    width: 18px;
    text-align: center;
    font-size: 0.95rem;
    color: #94a3b8;
    transition: color 0.15s ease;
}

.rpe-sidebar-item-label {
    flex: 1;
    min-width: 0;
}

.rpe-sidebar-count {
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    color: #94a3b8;
}

.rpe-sidebar-item--active .rpe-sidebar-count {
    color: rgba(255, 255, 255, 0.85);
}

/* ── Main ── */
.rpe-main {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: 100%;
}

/**
 * Même grille que les rangées : colonne texte extensible + pastille à taille
 * fixe. En flex, le libellé se repliait mot par mot dès que le conteneur était
 * mesuré en min-content.
 */
.rpe-group-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 18px 24px;
    border-bottom: 1px solid #e2e8f0;
    background: #fcfdfe;
    box-sizing: border-box;
}

.rpe-group-info {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
}

.rpe-group-icon {
    flex: 0 0 40px;
    width: 40px;
    min-width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #ecfeff;
    color: #0891b2;
    font-size: 1.1rem;
}

.rpe-group-text {
    min-width: 0;
}

.rpe-group-title,
.rpe-group-sub {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.rpe-group-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.3;
    color: #0f172a;
}

.rpe-group-sub {
    margin: 2px 0 0;
    font-size: 0.78rem;
    color: #94a3b8;
}

.rpe-group-sub-count {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: #64748b;
}

/**
 * Libellé + interrupteur, sans contour ni fond : le libellé dit ce qu'un clic
 * va faire, c'est l'interrupteur seul qui porte la couleur d'état.
 */
.rpe-select-all {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    margin: 0;
    padding: 0;
    flex: 0 0 auto;
    justify-self: end;
    cursor: pointer;
    white-space: nowrap;
}

.rpe-select-all-label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #475569;
    transition: color 0.15s ease;
}

.rpe-select-all:hover .rpe-select-all-label {
    color: #0f172a;
}

/* ── Liste des permissions ── */
.rpe-permission-list {
    flex: 1 1 auto;
    width: 100%;
    list-style: none;
    margin: 0;
    padding: 0;
    overflow-y: auto;
}

/**
 * Grille plutôt que flex : la colonne de gauche (`minmax(0, 1fr)`) absorbe
 * toute la largeur disponible, le toggle garde sa taille et reste collé au
 * bord droit, quelle que soit la longueur du libellé.
 */
.rpe-permission-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    width: 100%;
    min-height: 52px;
    padding: 10px 24px;
    border-bottom: 1px solid #f1f5f9;
    box-sizing: border-box;
    transition: background 0.1s ease;
}

.rpe-permission-row:hover {
    background: #f8fafc;
}

.rpe-permission-row--modified {
    background: #fefce8;
}

.rpe-permission-row--modified:hover {
    background: #fef9c3;
}

.rpe-permission-body {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 2px 10px;
    min-width: 0;
}

.rpe-permission-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    color: #334155;
}

.rpe-permission-lock {
    font-size: 0.72rem;
    color: #94a3b8;
}

/**
 * Pastille neutre : l'identifiant technique est une métadonnée, pas un statut.
 * Une couleur d'état (vert, rouge) lui donnerait un sens qu'il n'a pas et
 * concurrencerait le cyan des interrupteurs.
 */
.rpe-permission-note {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex-basis: 100%;
    font-size: 0.72rem;
    color: #94a3b8;
}

.rpe-permission-key {
    font-size: 0.68rem;
    line-height: 1.4;
    color: #64748b;
    font-family: var(--v2-font-mono, monospace);
    white-space: nowrap;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    padding: 1px 7px;
}

.rpe-permission-row--modified .rpe-permission-key {
    background: #fef9c3;
    border-color: #fde68a;
    color: #854d0e;
}

/* ── Toggle ── */
.rpe-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0;
    justify-self: end;
    flex: 0 0 auto;
}

.rpe-toggle input {
    display: none;
}

.rpe-toggle-slider {
    position: relative;
    width: 36px;
    height: 20px;
    background: #cbd5e1;
    border-radius: 20px;
    transition: background 0.2s ease;
}

.rpe-toggle-slider::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.rpe-toggle input:checked + .rpe-toggle-slider {
    background: #0891b2;
}

.rpe-toggle input:checked + .rpe-toggle-slider::after {
    transform: translateX(16px);
}

.rpe-toggle input:disabled + .rpe-toggle-slider {
    opacity: 0.5;
    cursor: not-allowed;
}

.rpe-toggle:hover .rpe-toggle-slider {
    background: #94a3b8;
}

.rpe-toggle:hover input:checked + .rpe-toggle-slider {
    background: #0e7490;
}

/* ── Empty ── */
.rpe-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    gap: 12px;
}

.rpe-empty-icon {
    font-size: 2.5rem;
}

/* ── Footer ── */
.rpe-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    box-sizing: border-box;
    padding: 16px 24px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
    margin-top: auto;
    flex-wrap: wrap;
}

.rpe-changes {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 20px;
    background: #fef3c7;
    color: #92400e;
    font-size: 0.8rem;
    font-weight: 500;
}

.rpe-footer-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

/* ── Responsive ── */
@media (max-width: 900px) {
    .rpe-layout {
        flex-direction: column;
        min-height: auto;
    }

    .rpe-sidebar {
        width: 100%;
        min-width: 100%;
        border-right: none;
        border-bottom: 1px solid #e2e8f0;
    }

    .rpe-sidebar-nav {
        flex-direction: row;
        overflow-x: auto;
        padding: 0 8px 12px;
        gap: 4px;
        -webkit-overflow-scrolling: touch;
    }

    .rpe-sidebar-item {
        white-space: nowrap;
        padding: 8px 14px;
        min-width: fit-content;
    }

    .rpe-group-header {
        padding: 16px;
    }

    .rpe-permission-row {
        padding: 10px 16px;
    }

    .rpe-footer {
        padding: 12px 16px;
    }
}

@media (max-width: 560px) {
    /* Sous cette largeur seulement, la pastille passe sous le titre. */
    .rpe-group-header {
        grid-template-columns: minmax(0, 1fr);
        gap: 12px;
    }

    .rpe-select-all {
        width: 100%;
        justify-self: stretch;
        justify-content: space-between;
    }

    .rpe-permission-body {
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
    }

    .rpe-permission-key {
        white-space: normal;
        word-break: break-all;
    }

    .rpe-permission-row {
        align-items: start;
    }
}

@media (max-width: 480px) {
    .rpe-footer-actions {
        width: 100%;
        justify-content: flex-end;
    }
}
</style>
